import { defineStore } from 'pinia'
import httpClient from '@/api/httpClient'
import { withIDKeys } from '@/utils/state_helpers'
import store from '@/store'
import { set, del } from 'vue'

let localIdCounter = 0

export const useStoryCustomFieldsStore = defineStore('storyCustomFields', {
  state: () => ({
    storyCustomFields: {},
    storyId: null
  }),

  getters: {
    all: ({ storyCustomFields }) => Object.values(storyCustomFields),
    byId: ({ storyCustomFields }) => (id) => storyCustomFields[id],
  },

  actions: {
    reset() {
      this.storyCustomFields = {}
      this.storyId = null
    },

    async fetchForStory(storyId) {
      this.storyId = storyId
      return httpClient.get(`/api/internal/stories/${storyId}/story_custom_fields`)
        .then(response => {
          this.storyCustomFields = withIDKeys(response.data.story_custom_fields)
        })
        .catch((err) => {
          store.dispatch('messages/smartAdd', err)
        })
    },

    // Add a new unsaved field locally (no API call)
    addLocal() {
      const localId = `new_${++localIdCounter}`
      set(this.storyCustomFields, localId, {
        id: localId,
        name: '',
        value: '',
        isNew: true
      })
      return localId
    },

    // Save a field (create if new, update if existing)
    async save(fieldId) {
      const field = this.storyCustomFields[fieldId]
      if (!field) return

      if (field.isNew) {
        // Create new record
        return httpClient.post(`/api/internal/stories/${this.storyId}/story_custom_fields`, {
          story_custom_field: { name: field.name, value: field.value }
        })
        .then(({ data: { story_custom_field }}) => {
          // Remove the local entry and add the saved one
          del(this.storyCustomFields, fieldId)
          set(this.storyCustomFields, story_custom_field.id, story_custom_field)
          store.dispatch('messages/addNotice', 'Custom field saved')
          return story_custom_field
        })
        .catch((err) => {
          store.dispatch('messages/smartAdd', err)
          throw err
        })
      } else {
        // Update existing record
        return httpClient.patch(`/api/internal/story_custom_fields/${fieldId}`, {
          story_custom_field: { name: field.name, value: field.value }
        })
        .then(({ data: { story_custom_field }}) => {
          set(this.storyCustomFields, story_custom_field.id, story_custom_field)
          store.dispatch('messages/addNotice', 'Custom field saved')
          return story_custom_field
        })
        .catch((err) => {
          store.dispatch('messages/smartAdd', err)
          throw err
        })
      }
    },

    async delete(fieldId) {
      const field = this.storyCustomFields[fieldId]
      if (!field) return

      // If it's a new unsaved field, just remove locally
      if (field.isNew) {
        del(this.storyCustomFields, fieldId)
        return
      }

      // Otherwise delete from server
      return httpClient.delete(`/api/internal/story_custom_fields/${fieldId}`)
        .then(() => {
          del(this.storyCustomFields, fieldId)
        })
        .catch((err) => {
          store.dispatch('messages/smartAdd', err)
        })
    },

    updateLocal(id, field, value) {
      if (this.storyCustomFields[id]) {
        set(this.storyCustomFields[id], field, value)
      }
    }
  }
})
